import getEventBoxHeight from '@sdk/schedule/helpers/getEventBoxHeight';
import type { SortMethodType } from '@sdk/schedule/types/SortMethodType';

import type { GroupsArray, AllGroup, DynamicGroup } from '../types/GroupsArrayType';
import type { RowType } from '../types/RowType';
import type { ScheduleFilters, OptionsFilterType, GetDayPartGroupDisplayNameType } from '../types';
import { ADD_EMPLOYEE_ROW_HEIGHT } from '../components/AddEmployeeRow';
import { LAST_ROW_SPACER_HEIGHT } from '../components/LastRowSpacer';

import extractFromGroupsArray from './helpers/extractFromGroupsArray';
import makeTopRows from './rows/makeTopRows';
import makeUserRows from './rows/makeUserRows';
import makeGroupedRows from './rows/makeGroupedRows';
import isSpecialGroupVisible from './helpers/isSpecialGroupVisible';
import { ScheduledShiftsHeader } from './rows/Headers';
import {
  SUMMARY_ROW_HEIGHT,
  UNASSIGNED,
  AVAILABLE,
  EVENT_TEXT_LINE_HEIGHT,
  BOX_VERTICAL_PADDING,
} from './constants';

export type RowEventCountProp = '__maxDayEventsCount' | '__maxWeekEventsCount';

type Config = {
  groupMethod: SortMethodType;
  showAddUser: boolean;
  personasDict: {
    [key in string]: any;
  };
  tags: Array<{
    id: number;
    name: string;
  }>;
  scheduleFilters: ScheduleFilters;
  eventNumberOfLines: number;
  optionsFilter: OptionsFilterType;
  getDayPartGroupDisplayName: GetDayPartGroupDisplayNameType;
};

export default function makeRows(groupsArray: GroupsArray, config: Config): Array<RowType> {
  const { unassigned, available, all, unassignedEvents, availableEvents } = extractFromGroupsArray(
    groupsArray,
    config.groupMethod
  );

  if (!all) {
    return [];
  }
  const {
    eventNumberOfLines,
    scheduleFilters,
    groupMethod,
    optionsFilter,
    getDayPartGroupDisplayName,
  } = config;
  const { showCountOfHours, showEmptyRows, showLaborCost, showLaborPercentage } = optionsFilter;

  const hideEmptyRowsFilter = !showEmptyRows;
  const showSummary = showCountOfHours || showLaborCost || showLaborPercentage;

  const eventHeight = getEventBoxHeight(eventNumberOfLines, {
    lineHeight: EVENT_TEXT_LINE_HEIGHT,
    verticalPadding: BOX_VERTICAL_PADDING,
  });

  const shouldDisplayUnassigned =
    unassigned && isSpecialGroupVisible(UNASSIGNED, scheduleFilters.eventTypes);
  const shouldDisplayAvailable =
    available && isSpecialGroupVisible(AVAILABLE, scheduleFilters.eventTypes);
  //TODO - hideEmptyRows should be false for month mode
  const hideEmptyRows = hideEmptyRowsFilter && groupMethod !== 'dtstart';
  //TODO - handle prop for day mode once we will have it
  const eventsCountProp: RowEventCountProp = '__maxWeekEventsCount';
  const rows = [];
  const rowIds = {};

  if (shouldDisplayUnassigned) {
    rows.push(...makeTopRows(UNASSIGNED, unassignedEvents, unassigned, eventHeight));
  }

  if (shouldDisplayAvailable) {
    rows.push(...makeTopRows(AVAILABLE, availableEvents, available, eventHeight));
  }

  if (groupMethod === 'username') {
    rows.push(ScheduledShiftsHeader);

    rows.push(
      ...makeUserRows(all as AllGroup, {
        eventHeight,
        hideEmptyRows,
        eventsCountProp,
        rowIds,
        eventNumberOfLines,
        optionsFilter,
      })
    );

    if (showSummary) {
      rows.push({
        id: 'UsersSummaryRow',
        rowType: 'SummaryRow',
        rowData: {
          size: SUMMARY_ROW_HEIGHT,
          totalsPerDay: (all as AllGroup).totalsPerDay,
        },
      });
    } else if (rows.length) {
      rows[rows.length - 1].rowData.withBottomSeparator = true;
    }

    if (config.showAddUser) {
      rows.push({
        id: 'AddUserRow',
        rowType: 'AddUserRow',
        rowData: {
          size: ADD_EMPLOYEE_ROW_HEIGHT,
        },
      });
    }
  } else {
    const groupRows = makeGroupedRows(all as Array<DynamicGroup>, {
      ...config,
      eventHeight,
      hideEmptyRows,
      showSummary,
      getDayPartGroupDisplayName,
      rowIds,
    });

    // @TODO: add new empty row type here when designed

    rows.push(...groupRows);
  }

  rows.push({
    id: 'LastRowSpacer',
    rowType: 'LastRowSpacer',
    rowData: {
      size: LAST_ROW_SPACER_HEIGHT,
    },
  });

  return rows;
}
